import { CiudadVivaEvent, EventCategory } from "./events";
import type { User } from "./store/auth-store";
import { api } from "./api-client";
import { toZonedTime } from "date-fns-tz";
import { detectRegionLocally, getChosenCountry, getSavedRegion } from "./detect-region";
import { DEFAULT_LOCALE, type Locale } from "./i18n/config";
import { getMessages } from "./i18n/messages";
import { currentLocale, localeHeaders } from "./i18n/request";
import dateMessages from "./api.i18n";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001/api";

// Agrupación categoría cruda -> categoría de vitrina: se pide al backend
// (GET /events/categories/display-map, único lugar que la define — ver
// DISPLAY_TO_DB_CATEGORIES en ciudad-server/events.service.ts) en vez de
// mantener acá una copia a mano que puede desincronizarse silenciosamente.
// Cache de 1h porque esta taxonomía casi no cambia.
// Países con eventos vigentes (selector de país global). [] si falla: el
// selector sigue con los países fijos.
export async function fetchActiveCountries(): Promise<string[]> {
  try {
    const res = await fetch(`${API_URL}/events/countries`, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) return [];
    const codes = (await res.json()) as unknown;
    return Array.isArray(codes) ? codes.filter((c): c is string => typeof c === "string" && /^[A-Z]{2}$/.test(c)) : [];
  } catch {
    return [];
  }
}

// revalidateSeconds: una página ISR se regenera según el MENOR revalidate de
// todos sus fetch, así que las páginas con caché larga (ficha de evento, 24 h)
// tienen que pasar el suyo también a este fetch y a los demás.
async function fetchCategoryDisplayMap(revalidateSeconds = 3600): Promise<Record<string, EventCategory>> {
  try {
    const res = await fetch(`${API_URL}/events/categories/display-map`, {
      next: { revalidate: revalidateSeconds },
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return {};
    return res.json();
  } catch {
    return {};
  }
}

// El evento no representa un instante real — es una fecha de calendario
// ("6 de agosto") que el backend entrega como string plano "DD-MM-YYYY" (ver
// ciudad-server PR #104). Por eso se parsea por posición, nunca con
// `new Date(string)`/`toZonedTime(string)`: cualquier conversión de zona
// horaria reinterpretaría el valor como si fuera un instante real y correría
// el día según el huso de quien lo lea. El único lugar donde SÍ corresponde
// una zona horaria real es al calcular "hoy"/"este fin de semana" — ahí sí
// importa el huso del evento, porque es una comparación contra el momento
// actual real.
function parseEventDate(value: string): { year: number; month: number; day: number } {
  const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    return { year: Number(y), month: Number(m) - 1, day: Number(d) };
  }
  // Fallback defensivo para strings ISO (ej. `createdAt`, usado solo cuando
  // falta date.start) — nunca debería llegar aquí un date.start/end real.
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return { year: y, month: m - 1, day: d };
}

function eventLocalDate(value: string): Date {
  const { year, month, day } = parseEventDate(value);
  return new Date(year, month, day);
}

// Convierte el "DD-MM-YYYY" del backend a "YYYY-MM-DD" — el formato interno
// que ya esperan lib/calendar.ts (agregar al calendario) y el resto del código.
function toIsoDateString(value: string): string {
  const { year, month, day } = parseEventDate(value);
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function todayInZone(timezone: string): Date {
  const zoned = toZonedTime(new Date(), timezone);
  return new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate());
}

function isTodayDate(dateStr: string, timezone = 'America/Santiago'): boolean {
  const d = eventLocalDate(dateStr);
  const today = todayInZone(timezone);
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}

function getUpcomingWeekend(timezone: string): { start: Date; end: Date } {
  const today = todayInZone(timezone);
  const day = today.getDay(); // 0=Dom, 1=Lun ... 6=Sáb

  const saturday = new Date(today);
  if (day === 0) {
    // Domingo: el sábado ya pasó, "este fin de semana" es solo hoy
    saturday.setDate(today.getDate() - 1);
  } else if (day === 6) {
    // Sábado: hoy mismo es el inicio del fin de semana
  } else {
    // Lunes a viernes: próximo sábado
    saturday.setDate(today.getDate() + (6 - day));
  }

  const monday = new Date(saturday);
  monday.setDate(saturday.getDate() + 2);
  return { start: saturday, end: monday };
}

function isUpcomingWeekend(dateStr: string, timezone = 'America/Santiago'): boolean {
  const d = eventLocalDate(dateStr);
  const { start, end } = getUpcomingWeekend(timezone);
  return d >= start && d < end;
}

// Las etiquetas de fecha se arman en el idioma de la página (lib/api.i18n.ts).
type DateMessages = ReturnType<typeof getMessages<(typeof dateMessages)["es"]>>;

function formatSingleDate(t: DateMessages, dateStr: string, timeObj?: { start?: string; end?: string }, timezone = 'America/Santiago'): string {
  const d = eventLocalDate(dateStr);
  const today = isTodayDate(dateStr, timezone);
  const dayLabel = today ? t.today : t.shortDate(t.weekdayShort[d.getDay()], d.getDate(), t.monthShort[d.getMonth()]);
  return timeObj?.start ? `${dayLabel} · ${timeObj.start}` : dayLabel;
}

// Formatea múltiples fechas: "6, 13, 20 y 27 de mayo"
function formatMultipleDates(t: DateMessages, dates: string[]): string {
  const parsed = dates.map(eventLocalDate);
  const days = parsed.map(d => d.getDate());
  const months = parsed.map(d => d.getMonth());

  // Si todas son del mismo mes
  if (months.every(m => m === months[0])) {
    return t.daysOfMonth(days, t.monthLong[months[0]]);
  }
  // Meses distintos
  return parsed.map(d => t.dayMonthLong(d.getDate(), t.monthLong[d.getMonth()])).join(", ");
}

// Retorna la próxima fecha futura del array (o la última si todas pasaron)
function nextDate(dates: string[], timezone = 'America/Santiago'): string {
  const today = todayInZone(timezone);
  const future = dates.filter(d => eventLocalDate(d) >= today);
  return future.length > 0 ? future[0] : dates[dates.length - 1];
}

function formatTime(timeObj?: { start?: string; end?: string }): string {
  if (!timeObj) return "";
  if (timeObj.start && timeObj.end) return `${timeObj.start} – ${timeObj.end}`;
  return timeObj.start ?? "";
}

function formatDateRange(t: DateMessages, startStr: string, endStr: string, timezone = 'America/Santiago'): string {
  const s = eventLocalDate(startStr);
  const e = eventLocalDate(endStr);

  // Si el inicio ya pasó, mostrar "Hasta X"
  if (s < todayInZone(timezone)) {
    return t.until(t.dayMonthShort(e.getDate(), t.monthShort[e.getMonth()]));
  }

  // Mismo mes: "6 – 11 Jun"
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return t.rangeSameMonth(s.getDate(), e.getDate(), t.monthShort[e.getMonth()]);
  }

  // Meses distintos: "14 May – 11 Jul"
  return t.range(
    t.dayMonthShort(s.getDate(), t.monthShort[s.getMonth()]),
    t.dayMonthShort(e.getDate(), t.monthShort[e.getMonth()]),
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapEvent(raw: any, categoryDisplayMap: Record<string, EventCategory>, lang: Locale = DEFAULT_LOCALE): CiudadVivaEvent {
  const t = getMessages(dateMessages, lang);
  const tz: string = raw.timezone ?? 'America/Santiago';
  const hasDates = Array.isArray(raw.dates) && raw.dates.length > 0;
  const startDate: string = raw.date?.start ?? raw.createdAt;

  // Para la card: mostrar la próxima fecha relevante
  const displayDate = hasDates ? nextDate(raw.dates, tz) : startDate;
  const endDate: string | undefined = raw.date?.end;
  const hasRange = endDate && eventLocalDate(endDate).getTime() !== eventLocalDate(startDate).getTime();

  const dateLabel = hasDates
    ? formatMultipleDates(t, raw.dates)
    : hasRange
    ? formatDateRange(t, startDate, endDate!, tz)
    : formatSingleDate(t, startDate, raw.time, tz);

  const todayFlag = hasDates
    ? raw.dates.some((d: string) => isTodayDate(d, tz))
    : isTodayDate(startDate, tz);

  const weekendFlag = hasDates
    ? raw.dates.some((d: string) => isUpcomingWeekend(d, tz))
    : isUpcomingWeekend(startDate, tz);

  return {
    slug: raw.slug,
    title: raw.title,
    image: raw.imageUrl ?? "/images/event-music.jpg",
    category: categoryDisplayMap[raw.category] ?? "Cultura",
    date: dateLabel,
    dateISO: toIsoDateString(displayDate),
    time: formatTime(raw.time),
    schedule: raw.schedule ?? undefined,
    region: raw.region ?? "Metropolitana",
    country: raw.country ?? "CL",
    district: raw.district ?? "",
    venue: raw.venue ?? "",
    isOnline: raw.isOnline ?? false,
    price: raw.price === 0 ? "free" : raw.price < 0 ? "consultar" : (raw.price as number),
    organizer: raw.organizationId?.name ?? raw.district ?? '',
    organizerSlug: raw.organizationId?.slug ?? undefined,
    organizerLogoUrl: raw.organizationId?.logoUrl ?? undefined,
    organizerWebsite: raw.organizationId?.website ?? undefined,
    organizerLinks: raw.organizationId?.links?.length ? raw.organizationId.links : undefined,
    organizerTrusted: raw.organizationId?.trusted ?? false,
    realOrganizerName: raw.organizerName ?? undefined,
    realOrganizerOrgSlug: raw.organizerOrgId?.slug ?? undefined,
    realOrganizerOrgLogoUrl: raw.organizerOrgId?.logoUrl ?? undefined,
    realOrganizerOrgTrusted: raw.organizerOrgId?.trusted ?? undefined,
    description: raw.description ?? "",
    tags: raw.tags ?? [],
    dates: hasDates ? raw.dates.map(toIsoDateString) : undefined,
    dateStartISO: toIsoDateString(startDate),
    dateEndISO: endDate ? toIsoDateString(endDate) : undefined,
    timeStart: raw.time?.start ?? undefined,
    timeEnd: raw.time?.end ?? undefined,
    recurrenceType: raw.recurrenceType ?? "none",
    today: todayFlag,
    weekend: weekendFlag,
    location: raw.location ?? undefined,
    sponsored: raw.promoted ?? false,
    ctaLabel: t.ctaLabel,
    ctaUrl: raw.eventUrl || undefined,
    status: raw.status ?? undefined,
    cancellationReason: raw.cancellationReason ?? undefined,
    canceledAt: raw.canceledAt ?? undefined,
    active: raw.active ?? true,
    timezone: raw.timezone,
    interestCount: raw.interestCount ?? 0,
  };
}

export async function fetchEvents(params?: {
  category?: string;
  dateFilter?: "today" | "weekend";
  priceFilter?: "free" | "paid" | "unknown";
  regions?: string[];
  districts?: string[];
  organizationIds?: string[];
  // Eventos puntuales por slug (p. ej. los de la sopa de letras).
  slugs?: string[];
  // País del selector global (CL, US…). Sin él el backend no filtra por país.
  country?: string;
  search?: string;
  page?: number;
  limit?: number;
  // Idioma de las etiquetas de fecha (no se envía al backend).
  lang?: Locale;
  // Caché del fetch (default 300 s). Ver fetchCategoryDisplayMap.
  revalidateSeconds?: number;
}): Promise<{ data: CiudadVivaEvent[]; meta: { total: number; page: number; totalPages: number } }> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set("category", params.category);
  if (params?.dateFilter) qs.set("dateFilter", params.dateFilter);
  if (params?.priceFilter) qs.set("priceFilter", params.priceFilter);
  if (params?.regions?.length) qs.set("regions", params.regions.join(","));
  if (params?.districts?.length) qs.set("districts", params.districts.join(","));
  if (params?.organizationIds?.length) qs.set("organizationIds", params.organizationIds.join(","));
  if (params?.slugs?.length) qs.set("slugs", params.slugs.join(","));
  if (params?.country) qs.set("country", params.country);
  if (params?.search) qs.set("search", params.search);
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));

  const [res, categoryDisplayMap] = await Promise.all([
    fetch(`${API_URL}/events?${qs.toString()}`, {
      next: { revalidate: params?.revalidateSeconds ?? 300 },
      signal: AbortSignal.timeout(8000),
    }),
    fetchCategoryDisplayMap(params?.revalidateSeconds),
  ]);
  if (!res.ok) throw new Error(getMessages(dateMessages, params?.lang ?? currentLocale()).errors.events);

  const json = await res.json();
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: json.data.map((raw: any) => mapEvent(raw, categoryDisplayMap, params?.lang)),
    meta: json.meta,
  };
}

export interface DistrictOption {
  name: string;
  count: number;
  // Código ISO del país (CL, US…). Opcional: un backend anterior no lo envía.
  country?: string;
}

// Sin opciones devuelve solo comunas chilenas (páginas SEO de comuna, home,
// sitemap). `country` → solo las ubicaciones de ese país (filtro "Ubicación"
// de /eventos y buscador del hero); `allCountries` → todas.
export async function fetchDistricts(
  organizationIds?: string[],
  opts?: { revalidateSeconds?: number; allCountries?: boolean; country?: string },
): Promise<DistrictOption[]> {
  let url = `${API_URL}/districts?withEvents=true`;
  if (organizationIds?.length) url += `&organizations=${organizationIds.join(',')}`;
  if (opts?.allCountries) url += `&allCountries=true`;
  if (opts?.country) url += `&country=${opts.country}`;
  const res = await fetch(url, {
    ...(opts?.revalidateSeconds
      ? { next: { revalidate: opts.revalidateSeconds } }
      : { cache: "no-store" as const }),
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) throw new Error(getMessages(dateMessages, currentLocale()).errors.districts);
  return res.json();
}

export async function fetchOrganizations(
  districts?: string[],
  opts?: { revalidateSeconds?: number; includeEmpty?: boolean; country?: string },
): Promise<{ _id: string; slug: string; name: string; logoUrl?: string; district?: string; country?: string; trusted: boolean; eventCount: number }[]> {
  let url = `${API_URL}/organizations/directory`;
  const params = new URLSearchParams();
  if (districts?.length) params.set("districts", districts.join(","));
  if (opts?.includeEmpty) params.set("all", "true");
  if (opts?.country) params.set("country", opts.country);
  if (params.size) url += `?${params.toString()}`;
  const res = await fetch(url, {
    ...(opts?.revalidateSeconds
      ? { next: { revalidate: opts.revalidateSeconds } }
      : { cache: "no-store" as const }),
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return [];
  return res.json();
}

// ─── Sugerencias ────────────────────────────────────────────────────────────

export type SuggestionStatus = "under_review" | "planned" | "in_progress" | "done" | "not_planned";

export interface Suggestion {
  _id: string;
  title: string;
  description: string;
  authorName: string;
  authorAvatarUrl?: string;
  // Enviada sin cuenta: authorName es el alias o ANONYMOUS_AUTHOR.
  anonymous?: boolean;
  status: SuggestionStatus;
  statusNote?: string;
  voteCount: number;
  createdAt: string;
}

// revalidateSeconds: la página /sugerencias la trae cacheada (el tablero se
// refresca solo en el navegador); sin él, siempre al día.
export async function fetchSuggestions(
  status?: SuggestionStatus,
  opts?: { revalidateSeconds?: number },
): Promise<Suggestion[]> {
  let url = `${API_URL}/suggestions`;
  if (status) url += `?status=${status}`;
  const res = await fetch(url, {
    ...(opts?.revalidateSeconds ? { next: { revalidate: opts.revalidateSeconds } } : { cache: "no-store" as const }),
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return [];
  return res.json();
}

export async function fetchMySuggestionVotes(): Promise<string[]> {
  return api.get(`/suggestions/my-votes`);
}

export async function createSuggestion(title: string, description: string): Promise<Suggestion> {
  return api.post(`/suggestions`, { title, description });
}

// Nombre que guarda el backend para una sugerencia sin cuenta y sin alias
// (se muestra traducido).
export const ANONYMOUS_AUTHOR = "Anónimo";

// Sin cuenta: alias opcional; `website` es el campo trampa contra bots (va
// vacío desde la web).
export async function createAnonymousSuggestion(
  title: string,
  description: string,
  alias: string,
  website: string,
): Promise<Suggestion> {
  return api.post(`/suggestions/anonymous`, {
    title,
    description,
    ...(alias.trim() && { alias: alias.trim() }),
    ...(website && { website }),
  });
}

export async function toggleSuggestionVote(id: string): Promise<{ voted: boolean; voteCount: number }> {
  return api.post(`/suggestions/${id}/vote`, {});
}

export interface OrganizationProfile {
  _id: string;
  slug: string;
  name: string;
  type: "municipality" | "private";
  district?: string;
  logoUrl?: string;
  website?: string;
  websiteName?: string;
  links?: Array<{ name: string; url: string }>;
  instagramHandle?: string;
  tiktokHandle?: string;
  xHandle?: string;
  contactEmail?: string;
  description?: string;
  trusted: boolean;
  createdAt: string;
}

export async function fetchOrganizationProfile(
  slug: string,
  opts?: { revalidateSeconds?: number },
): Promise<OrganizationProfile | null> {
  // revalidate:60 (no no-store) — cada visita a esta página es también un
  // request de Googlebot al recorrer el sitemap (miles de organizaciones);
  // 60s de staleness máxima es imperceptible para un perfil público que
  // cambia poco, y evita pegarle al backend en cada crawl.
  const res = await fetch(`${API_URL}/organizations/${slug}/profile`, {
    next: { revalidate: opts?.revalidateSeconds ?? 60 },
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return null;
  return res.json();
}

export async function fetchEventBySlug(
  slug: string,
  lang: Locale = DEFAULT_LOCALE,
  opts?: { revalidateSeconds?: number },
): Promise<CiudadVivaEvent | null> {
  // revalidate:60, no no-store — el revalidate:3600 anterior (antes de que
  // esto pasara a no-store) dejaba servir hasta 1h de datos viejos
  // (interestCount, cancelaciones, cambios de precio). 60s es un punto
  // medio: sigue imperceptible para un usuario real, pero evita que cada
  // request de Googlebot (miles de eventos en el sitemap) le pegue en vivo
  // al backend.
  const [res, categoryDisplayMap] = await Promise.all([
    fetch(`${API_URL}/events/${slug}`, {
      next: { revalidate: opts?.revalidateSeconds ?? 60 },
      signal: AbortSignal.timeout(8000),
    }),
    fetchCategoryDisplayMap(opts?.revalidateSeconds),
  ]);
  if (!res.ok) return null;
  return mapEvent(await res.json(), categoryDisplayMap, lang);
}

// --- Event lifecycle actions ---

export async function cancelEvent(slug: string, reason?: string): Promise<void> {
  await api.post(`/events/${slug}/cancel`, { reason });
}

export async function reactivateEvent(slug: string): Promise<void> {
  await api.post(`/events/${slug}/reactivate`, {});
}

export async function deleteEvent(slug: string): Promise<void> {
  await api.delete(`/events/${slug}`);
}

export async function submitForReview(slug: string): Promise<void> {
  await api.post(`/events/${slug}/submit`, {});
}

export async function toggleEventInterest(slug: string): Promise<{ interested: boolean; count: number }> {
  return api.post(`/events/${slug}/interest`, {});
}

export async function fetchMyInterestedSlugs(): Promise<string[]> {
  return api.get(`/events/interests`);
}

export interface InterestFeedResponse {
  data: CiudadVivaEvent[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// Feed paginado de "me interesa", más reciente primero — mismo endpoint que
// consume la pestaña "Interés" del mobile (ver ciudad-server PR #159).
export async function fetchMyInterestFeed(page = 1, limit = 20, lang: Locale = DEFAULT_LOCALE): Promise<InterestFeedResponse> {
  const [json, categoryDisplayMap] = await Promise.all([
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    api.get<{ data: any[]; meta: InterestFeedResponse["meta"] }>(
      `/events/interests/feed?page=${page}&limit=${limit}`,
    ),
    fetchCategoryDisplayMap(),
  ]);
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: json.data.map((raw: any) => mapEvent(raw, categoryDisplayMap, lang)),
    meta: json.meta,
  };
}

export async function fetchInterestCounts(slugs: string[]): Promise<Record<string, number>> {
  if (!slugs.length) return {};
  const res = await fetch(`${API_URL}/events/interest-counts?slugs=${slugs.join(",")}`, {
    cache: "no-store",
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return {};
  return res.json();
}

// --- Auth API functions ---

export async function registerUser(data: {
  email: string;
  password: string;
  name: string;
}): Promise<User> {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...localeHeaders() },
    body: JSON.stringify({ ...data, language: currentLocale() }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Error ${res.status}`);
  }
  const body = await res.json();
  return { ...body, id: body._id };
}

export async function loginUser(data: {
  email: string;
  password: string;
}): Promise<User> {
  // `language`: los correos le llegan en el último idioma que usó en la web.
  const res = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...localeHeaders() },
    body: JSON.stringify({ ...data, language: currentLocale() }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Error ${res.status}`);
  }
  const body = await res.json();
  return { ...body, id: body._id };
}

export async function logoutUser(): Promise<void> {
  await fetch(`${API_URL}/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
}

export async function forgotPassword(email: string): Promise<void> {
  const res = await fetch(`${API_URL}/auth/forgot-password`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...localeHeaders() },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Error ${res.status}`);
  }
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const res = await fetch(`${API_URL}/auth/reset-password`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...localeHeaders() },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Error ${res.status}`);
  }
}

export async function updateProfile(data: {
  name?: string;
  avatarUrl?: string;
  language?: Locale;
}): Promise<User> {
  const body = await api.patch<Record<string, unknown>>("/users/me", data);
  return { ...body, id: body._id } as unknown as User;
}

export async function changeUserPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api.patch("/users/me/password", { currentPassword, newPassword });
}

// ─── Viva Cine (dónde ver una película o serie) ────────────────────────────

// TMDB numera películas y series por separado (la película 1399 y la serie
// 1399 son cosas distintas): el tipo viaja siempre junto al id.
export type MediaType = "movie" | "tv";

export interface MovieSearchResult {
  id: number;
  // Opcional por compatibilidad: sin él, es una película.
  mediaType?: MediaType;
  title: string;
  year?: number;
  posterUrl?: string;
  voteAverage?: number;
  voteCount?: number;
}

export interface MovieProvider {
  providerName: string;
  logoUrl: string;
  // Búsqueda de la película en la plataforma, o la página "dónde ver" de
  // TMDB si la plataforma no tiene búsqueda por URL (lo decide el backend).
  url?: string;
}

export interface MovieProvidersResult {
  flatrate: MovieProvider[];
  rent: MovieProvider[];
  buy: MovieProvider[];
  hasAnyProviders: boolean;
}

export interface TrailerOption {
  youtubeKey: string;
  // "es", "en"... y país del doblaje ("MX", "ES"...), si TMDB lo informa.
  language: string;
  country?: string;
  type: "Trailer" | "Teaser";
  official: boolean;
}

export interface MovieDetail extends MovieSearchResult {
  overview?: string;
  genres?: string[];
  // IDs de TMDB de los géneros (no cambian con el idioma). Un backend
  // anterior no los envía.
  genreIds?: number[];
  runtimeMinutes?: number;
  tagline?: string;
  originalTitle?: string;
  backdropUrl?: string;
  director?: string;
  composer?: string;
  cast?: string[];
  certification?: string;
  // Trailers de YouTube que se pueden insertar (el backend ya descartó los
  // privados/borrados), en su orden. Se elige uno con pickTrailer
  // (lib/movies.ts) según idioma y país.
  trailers?: TrailerOption[];
  // Solo series.
  numberOfSeasons?: number;
  numberOfEpisodes?: number;
  // Ya traducido por el backend: "En emisión", "Finalizada", "Cancelada"...
  status?: string;
  nextEpisodeDate?: string; // "YYYY-MM-DD"
  creators?: string[];
  networks?: string[];
}

// Ruta del backend para un título: las series viven en /movies/tv/:id.
function titlePath(id: number, mediaType: MediaType = "movie"): string {
  return mediaType === "tv" ? `/movies/tv/${id}` : `/movies/${id}`;
}

interface TitleFetchOptions {
  revalidateSeconds?: number;
  mediaType?: MediaType;
  // Idioma de los textos de TMDB (títulos, sinopsis, géneros).
  lang?: Locale;
}

// El backend entrega español por defecto: el parámetro solo viaja para otros
// idiomas, así las URLs en español quedan iguales (misma caché que antes).
function langParam(lang?: Locale): string {
  return lang && lang !== DEFAULT_LOCALE ? `&lang=${lang}` : "";
}

// La tarjeta de película (components/movie-card) — viene pre-enriquecida
// con providers, tanto en populares como en la búsqueda (ver GET
// /movies/popular y /movies/search en el backend).
export interface PopularMovieCard extends MovieDetail {
  providers: MovieProvidersResult;
}

// null = la película no existe en TMDB (404 real) — lo distinguimos de un
// error de red/servicio para que la página pueda hacer notFound().
// revalidateSeconds: el catálogo de streaming no cambia a cada minuto, así
// que las llamadas server-side (generateMetadata, page.tsx) cachean 24h —
// las llamadas client-side (cambio manual de región) siguen sin caché.
export async function fetchMovieDetail(
  id: number,
  region: string,
  opts?: TitleFetchOptions,
): Promise<MovieDetail | null> {
  const res = await fetch(`${API_URL}${titlePath(id, opts?.mediaType)}?region=${region}${langParam(opts?.lang)}`, {
    ...(opts?.revalidateSeconds
      ? { next: { revalidate: opts.revalidateSeconds } }
      : { cache: "no-store" as const }),
    signal: AbortSignal.timeout(8000),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(getMessages(dateMessages, opts?.lang ?? currentLocale()).errors.title);
  return res.json();
}

export interface DetectedRegion {
  countryCode: string;
  countryName?: string;
}

// Catálogo de Viva Cine, en orden: el país elegido a mano en Viva Cine ("ver
// catálogo de otro país"), el país elegido en el selector global y, si no
// eligió nada, el deducido en el propio dispositivo (zona horaria → idioma →
// CL), sin enviar la IP a ningún tercero — ver lib/detect-region.ts (Ley
// 21.719). Se mantiene async y la misma forma de respuesta para no tocar a
// quienes la usan.
export async function detectMovieRegion(): Promise<DetectedRegion> {
  return { countryCode: getSavedRegion() ?? getChosenCountry() ?? detectRegionLocally() };
}

export async function searchMovies(
  title: string,
  region: string,
  lang?: Locale,
): Promise<PopularMovieCard[]> {
  const res = await fetch(
    `${API_URL}/movies/search?title=${encodeURIComponent(title)}&region=${region}&includeTv=1${langParam(lang)}`,
    { cache: "no-store", signal: AbortSignal.timeout(8000) },
  );
  if (!res.ok) return [];
  return res.json();
}

// Páginas de 20 títulos ("Cargar más"); sin `page`, la primera.
export async function fetchPopularMovies(
  region: string,
  opts?: { revalidateSeconds?: number; lang?: Locale; page?: number },
): Promise<PopularMovieCard[]> {
  const page = opts?.page && opts.page > 1 ? `&page=${opts.page}` : "";
  const res = await fetch(`${API_URL}/movies/popular?region=${region}&includeTv=1${langParam(opts?.lang)}${page}`, {
    ...(opts?.revalidateSeconds
      ? { next: { revalidate: opts.revalidateSeconds } }
      : { cache: "no-store" as const }),
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return [];
  return res.json();
}

export async function fetchMovieProviders(
  id: number,
  region: string,
  opts?: TitleFetchOptions,
): Promise<MovieProvidersResult> {
  const res = await fetch(`${API_URL}${titlePath(id, opts?.mediaType)}/providers?region=${region}${langParam(opts?.lang)}`, {
    ...(opts?.revalidateSeconds
      ? { next: { revalidate: opts.revalidateSeconds } }
      : { cache: "no-store" as const }),
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) throw new Error(getMessages(dateMessages, opts?.lang ?? currentLocale()).errors.providers);
  return res.json();
}

export async function fetchSimilarMovies(
  id: number,
  opts?: TitleFetchOptions,
): Promise<MovieSearchResult[]> {
  const res = await fetch(`${API_URL}${titlePath(id, opts?.mediaType)}/similar?${langParam(opts?.lang).slice(1)}`, {
    ...(opts?.revalidateSeconds
      ? { next: { revalidate: opts.revalidateSeconds } }
      : { cache: "no-store" as const }),
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return [];
  return res.json();
}

// Banda sonora: fragmento oficial de 30 s (Deezer). El link `previewUrl`
// VENCE a los ~15 min, por eso se pide siempre desde el navegador y sin
// caché (no puede quedar guardado en la página estática).
export interface Soundtrack {
  trackId: number;
  title: string;
  artist: string;
  album: string;
  previewUrl: string;
  deezerUrl: string;
}

export async function fetchSoundtrack(id: number, mediaType: MediaType = "movie"): Promise<Soundtrack | null> {
  try {
    const res = await fetch(`${API_URL}${titlePath(id, mediaType)}/soundtrack`, {
      cache: "no-store",
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { soundtrack: Soundtrack | null };
    return data.soundtrack;
  } catch {
    return null;
  }
}

// --- Panoramas ---

export type PanoramaKind = "event" | "movie" | "series";
export type PanoramaInviteeRole = "guest" | "editor";
// Solo aplica a invitados CON cuenta — un invitado sin cuenta accede de
// forma anónima por link y no tiene nada que aceptar/rechazar.
export type PanoramaInviteeStatus = "pending" | "accepted";
export type PanoramaReminderPreset = "1_day_before" | "3_hours_before" | "1_hour_before";


export interface PanoramaSummary {
  title: string;
  imageUrl?: string;
  // Solo si kind==='event' — para linkear al evento real (/evento/:slug).
  eventSlug?: string;
}

// Los nombres de cada tipo de panorama y de cada recordatorio (por idioma)
// están en app/[lang]/(dashboard)/mis-panoramas/[id]/page.i18n.ts.

export interface PanoramaInvitee {
  // Id opaco para quitar / cambiar rol (nunca el email en la URL).
  id: string;
  // Nombre de la cuenta Ciudad Viva, solo si el invitado tiene cuenta.
  name?: string;
  // Completo para creator/editor; enmascarado ("ju•••@gmail.com") para un
  // invitado 'guest' — el backend decide según el rol de quien consulta.
  email: string;
  hasAccount: boolean;
  role: PanoramaInviteeRole;
  addedAt: string;
  status?: PanoramaInviteeStatus;
}

export interface PanoramaReminder {
  offsetPreset: PanoramaReminderPreset;
  sentAt?: string;
}

export interface PanoramaListItem {
  id: string;
  kind: PanoramaKind;
  date: string;
  timeStart?: string;
  timeEnd?: string;
  timezone: string;
  customTitle?: string;
  summary: PanoramaSummary;
  tmdbMovieId?: number;
  tmdbSeriesId?: number;
  isCreator: boolean;
  myRole: "creator" | "editor" | "guest";
  // Nombre del creador — en "withFriends" es "quién me invitó".
  creatorName: string;
  // Solo en panoramas ajenos ("withFriends") donde soy invitado con cuenta —
  // 'pending' hasta que acepte/rechace la invitación.
  inviteStatus?: PanoramaInviteeStatus;
}

// invitees SÍ se muestra a cualquier invitado (incluso 'guest', sin poder
// gestionar nada) — solo `reminders` queda exclusivo de creator/editor.
export interface PanoramaDetail {
  id: string;
  kind: PanoramaKind;
  date: string;
  timeStart?: string;
  timeEnd?: string;
  timezone: string;
  customTitle?: string;
  note?: string;
  summary: PanoramaSummary;
  tmdbMovieId?: number;
  tmdbSeriesId?: number;
  viewerRole: "creator" | "editor" | "guest";
  creatorName: string;
  viewerInviteStatus?: PanoramaInviteeStatus;
  invitees?: PanoramaInvitee[];
  reminders?: PanoramaReminder[];
}

export interface PublicPanoramaView {
  kind: PanoramaKind;
  date: string;
  timeStart?: string;
  timeEnd?: string;
  timezone: string;
  customTitle?: string;
  note?: string;
  summary: PanoramaSummary;
  tmdbMovieId?: number;
  tmdbSeriesId?: number;
}

export interface CreatePanoramaInput {
  kind: PanoramaKind;
  // Slug público del evento (no el _id de Mongo, que nunca se expone al
  // frontend) — el backend resuelve el ObjectId internamente.
  eventSlug?: string;
  tmdbMovieId?: number;
  tmdbSeriesId?: number;
  date: string;
  timeStart?: string;
  timeEnd?: string;
  timezone?: string;
  customTitle?: string;
  note?: string;
}

// El backend no permite cambiar de qué trata un panorama después de
// creado (evento/película quedan fijos) — solo fecha/hora/copy.
export interface UpdatePanoramaInput {
  date?: string;
  timeStart?: string;
  timeEnd?: string;
  timezone?: string;
  customTitle?: string;
  note?: string;
}

export async function fetchMyPanoramas(): Promise<{
  own: PanoramaListItem[];
  withFriends: PanoramaListItem[];
}> {
  return api.get(`/panoramas/mine`);
}

export async function fetchPanorama(id: string): Promise<PanoramaDetail> {
  return api.get(`/panoramas/${id}`);
}

// Fetch público (sin credenciales) — mismo estilo que fetchEventBySlug, para
// la vista de solo lectura de un invitado sin cuenta.
export async function fetchPublicPanorama(
  token: string,
  lang: Locale = DEFAULT_LOCALE,
): Promise<PublicPanoramaView | null> {
  // Accept-Language: el título de una película/serie viene de TMDB en ese idioma.
  const res = await fetch(`${API_URL}/panoramas/public/${token}`, {
    headers: localeHeaders(lang),
    cache: "no-store",
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) return null;
  return res.json();
}

// create/update/duplicate devuelven el Document de Mongoose crudo (no la
// vista redactada de fetchPanorama/getDetail) — solo tipamos lo mínimo que
// realmente se usa (el _id, para navegar al detalle recién creado). Para
// mostrar el panorama después de estas acciones, volver a pedirlo con
// fetchPanorama().
export async function createPanorama(input: CreatePanoramaInput): Promise<{ _id: string }> {
  return api.post(`/panoramas`, input);
}

export async function updatePanorama(id: string, input: UpdatePanoramaInput): Promise<void> {
  await api.patch(`/panoramas/${id}`, input);
}

export async function deletePanorama(id: string): Promise<void> {
  await api.delete(`/panoramas/${id}`);
}

export async function duplicatePanorama(id: string, date: string): Promise<{ _id: string }> {
  return api.post(`/panoramas/${id}/duplicate`, { date });
}

export interface AddPanoramaInviteeResult {
  invitee: PanoramaInvitee;
  // Único momento en que el link (con el token embebido, si es invitado sin
  // cuenta) sale del backend — úsalo para ofrecer "compartir por WhatsApp"
  // ahí mismo; ningún GET posterior lo vuelve a exponer.
  shareUrl: string;
  // false = la invitación quedó guardada pero el correo no salió: ofrecer
  // compartir el link. Opcional por compatibilidad con backends anteriores.
  emailSent?: boolean;
}

export async function addPanoramaInvitee(
  id: string,
  email: string,
  role?: PanoramaInviteeRole,
): Promise<AddPanoramaInviteeResult> {
  return api.post(`/panoramas/${id}/invitees`, { email, role });
}

export async function removePanoramaInvitee(id: string, inviteeId: string): Promise<void> {
  await api.delete(`/panoramas/${id}/invitees/${encodeURIComponent(inviteeId)}`);
}

export async function setPanoramaInviteeRole(
  id: string,
  inviteeId: string,
  role: PanoramaInviteeRole,
): Promise<void> {
  await api.patch(`/panoramas/${id}/invitees/${encodeURIComponent(inviteeId)}/role`, { role });
}

// Invitado SIN cuenta: se quita del panorama y se borran sus datos (link
// "no quiero participar" del correo). Sin sesión — el token es su credencial.
export async function leavePublicPanorama(token: string): Promise<void> {
  const res = await fetch(`${API_URL}/panoramas/public/${token}/leave`, {
    method: "POST",
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) {
    const t = getMessages(dateMessages, currentLocale()).errors;
    throw new Error(res.status === 404 ? t.leaveInvalid : t.leaveFailed);
  }
}

export async function addPanoramaReminder(id: string, offsetPreset: PanoramaReminderPreset): Promise<void> {
  await api.post(`/panoramas/${id}/reminders`, { offsetPreset });
}

export async function removePanoramaReminder(id: string, offsetPreset: PanoramaReminderPreset): Promise<void> {
  await api.delete(`/panoramas/${id}/reminders/${offsetPreset}`);
}

// Autoservicio del propio invitado — aceptar/rechazar la invitación a un
// panorama ajeno (ver "Panoramas con amigos" en /mis-panoramas).
export async function acceptPanoramaInvite(id: string): Promise<void> {
  await api.post(`/panoramas/${id}/invite/accept`, {});
}

export async function declinePanoramaInvite(id: string): Promise<void> {
  await api.post(`/panoramas/${id}/invite/decline`, {});
}

